import { type ReactNode, createContext, useCallback, useEffect, useMemo, useState } from "react";
import ReconnectingWebSocket from "reconnecting-websocket";
import { Connection } from "sharedb/lib/client";
import { Socket } from "sharedb/lib/sharedb";

import { MonitoringEventTypeType } from "../shared/types/monitoring";
import type { WebsocketStatusType } from "../shared/types/pubsub";
import "../shared/worksheets/ot-types/ot-patch";
import { patchShareDBConnection } from "../shared/worksheets/sharedb-patch";
import { type ConnectionState, setupConnectionListeners } from "../sharedb/ShareDBConnection";

import { PingComponent } from "./PingComponent";

import { t } from "i18n/i18n";
import { useAppSelector } from "store/index";
import { selectCurrentSectionId, selectCurrentUserId, selectUserJWT } from "store/selectors";
import { useCreateMonitoringEvent } from "utils/hooks/use-create-monitoring-event";
import { logInfoWithDebugFlag } from "utils/logger";

// Patches ShareDB to ignore non-ShareDB messages (pong, version-update)
patchShareDBConnection();

// The minimum delay should be longer than the longest time we expect congested
// traffic to delay a message. Otherwise, ShareDB will think the connection is
// lost and try to reconnect, which can cause a loop of reconnecting and disconnecting.
const MIN_RECONNECTION_DELAY_MS = 3000;
// The maximum number of times we will try to reconnect before giving up (~5 minutes)
const MAX_RETRY_TIMES = 32;

// eslint-disable-next-line react-refresh/only-export-components
export const globalShareDBState: { connection: Connection | null } = {
  connection: null,
};

// TODO: This should be unified with ConnectionStatusIndicator states
// eslint-disable-next-line react-refresh/only-export-components
export const SHAREDB_STATUS_MESSAGE: Record<WebsocketStatusType, string> = {
  connecting: t("websocket.connecting"),
  connected: t("websocket.connected"),
  disconnected: t("websocket.disconnected"),
  closed: t("websocket.closed"),
  stopped: t("websocket.stopped"),
  401: t("websocket.401"),
  failed: t("websocket.failed"),
};

export interface ShareDBContextType {
  shareDBConnection: Connection | null;
  shareDBStatus: WebsocketStatusType;
  shareDBStatusReason: string;
  shareDBSectionId: string;
  retryShareDBConnection: () => void;
}

// eslint-disable-next-line react-refresh/only-export-components
export const ShareDBContext = createContext<ShareDBContextType | null>(null);

/**
 * ShareDBProvider manages the ShareDB websocket connection for real-time
 * collaborative document editing within a course.
 *
 * This is separate from PubsubWebsocketProvider to ensure ShareDB OT operations
 * are not blocked by pubsub message traffic in high-latency environments.
 */
export const ShareDBProvider = ({ children }: { children: ReactNode }) => {
  const sectionId = useAppSelector(selectCurrentSectionId);
  const jwt = useAppSelector(selectUserJWT);
  const userId = useAppSelector(selectCurrentUserId);

  const [shareDBConnection, setShareDBConnection] = useState<Connection | null>(null);
  const [websocketErrorCode, setWebsocketErrorCode] = useState<"401" | null>(null);
  const [shareDBStatus, setShareDBStatus] = useState<WebsocketStatusType>("connecting");
  const [shareDBSectionId, setShareDBSectionId] = useState<string>("");
  const [shareDBStatusReason, setShareDBStatusReason] = useState<string>("");
  const [webSocket, setWebSocket] = useState<ReconnectingWebSocket | null>(null);
  const [connectionKey, setConnectionKey] = useState(0);

  const createMonitoringEvent = useCreateMonitoringEvent();

  const retryShareDBConnection = useCallback(() => {
    setConnectionKey((key) => key + 1);
  }, []);

  // Compute status from error code or ShareDB connection state
  const computedStatus: WebsocketStatusType = websocketErrorCode || shareDBStatus;

  const contextValue = useMemo(
    () => ({
      shareDBConnection,
      shareDBStatus: computedStatus,
      shareDBSectionId,
      shareDBStatusReason,
      retryShareDBConnection,
    }),
    [
      shareDBConnection,
      computedStatus,
      shareDBSectionId,
      shareDBStatusReason,
      retryShareDBConnection,
    ],
  );

  useEffect(() => {
    if (!jwt || !userId || !sectionId) return;

    // Clear the global connection state immediately when starting a new connection
    // This ensures old connection handlers see the state is cleared even if they fire
    // synchronously during cleanup
    globalShareDBState.connection = null;

    if (shareDBConnection) {
      logInfoWithDebugFlag(
        "debug_sharedb",
        "Closing old sharedb connection before creating a new one",
        { sectionId },
      );

      // Close the old connection to prevent the socket from reconnecting.
      // This handles the case where a socket was reconnected after cleanup already ran but where the socket is
      // orphaned.
      shareDBConnection.close();
    }

    const socketUrl = `${window._env_.SHAREDB_WEBSOCKET_URL}?jwt=${jwt}&section_id=${sectionId}`;
    const socket = new ReconnectingWebSocket(`${socketUrl}`, [], {
      // ShareDB handles dropped messages, and buffering them while the socket
      // is closed has undefined behavior
      maxRetries: MAX_RETRY_TIMES,
      maxEnqueuedMessages: 0,
      minReconnectionDelay: MIN_RECONNECTION_DELAY_MS,
    });

    setWebSocket(socket);

    // Initialize status for new connection
    setShareDBStatus("connecting");
    setShareDBSectionId(sectionId);

    // It's important to initiate the connection on the socket _before_ the socket
    // finishes connecting, otherwise sharedb will miss the first message event and get stuck.
    const _shareDB = new Connection(socket as Socket);

    // Store the active connection in the global state so we can track it
    globalShareDBState.connection = _shareDB;
    setShareDBConnection(_shareDB);

    // Setup ShareDB connection events using the extracted handler
    const handleShareDBStateChange = (newState: ConnectionState, reason: string) => {
      // Only update status if this is still the active connection
      if (_shareDB === globalShareDBState.connection) {
        logInfoWithDebugFlag("debug_sharedb", "ShareDB connection state changed", {
          sectionId,
          userId,
          newState,
          reason,
        });

        setShareDBStatusReason(reason === "undefined" ? "" : reason);
        setShareDBStatus(newState);
        setShareDBSectionId(sectionId || "");
      } else {
        logInfoWithDebugFlag("debug_sharedb", "Old ShareDB connection state changed", {
          sectionId,
          userId,
          newState,
          reason,
        });
      }
    };

    setupConnectionListeners(_shareDB, {
      sectionId,
      userId,
      onStateChange: handleShareDBStateChange,
      onError: setWebsocketErrorCode,
    });

    // ShareDB has no "retries exhausted" state; check retryCount on each close to detect it.
    const handleSocketFailed = () => {
      if (globalShareDBState.connection !== _shareDB) return;
      if (socket.retryCount >= MAX_RETRY_TIMES) {
        logInfoWithDebugFlag("debug_sharedb", "ShareDB websocket retries exhausted", {
          sectionId,
          userId,
        });
        setShareDBStatus("failed");
      }
    };
    socket.addEventListener("close", handleSocketFailed);

    return () => {
      socket.removeEventListener("close", handleSocketFailed);

      // Clear global state before closing so old handlers don't see themselves as active
      // even if they fire synchronously during close()
      if (globalShareDBState.connection === _shareDB) {
        globalShareDBState.connection = null;
      }

      logInfoWithDebugFlag("debug_sharedb", "Closing ShareDB connection due to unmount", {
        sectionId,
      });

      // Sharedb is responsible for cleaning up the socket, so we don't close it separately
      _shareDB.close();
    };

    // This component only uses "sharedbConnection" to do a conditional log, and also sets it,
    // so we don't want to cause a loop by re-rendering when it changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jwt, userId, sectionId, connectionKey]);

  // Track sharedb connection status changes
  useEffect(() => {
    if (!shareDBSectionId) return;

    if (shareDBStatus === "connected") {
      createMonitoringEvent(MonitoringEventTypeType.WEBSOCKET_CONNECTED, {
        connection: "sharedb",
      });
    } else if (shareDBStatus === "disconnected") {
      // Active connection disconnected - this is unexpected
      if (document.hasFocus()) {
        createMonitoringEvent(MonitoringEventTypeType.WEBSOCKET_DISCONNECTED, {
          reason: shareDBStatusReason,
          connection: "sharedb",
        });
      }
    } else if (shareDBStatus !== "connecting") {
      createMonitoringEvent(MonitoringEventTypeType.WEBSOCKET_ERROR, {
        websocketStatus: shareDBStatus,
        connection: "sharedb",
      });
    }

    // We only want to create these events when the websocket status changes and not
    // when shareDBStatusReason changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareDBStatus, createMonitoringEvent]);

  return (
    <>
      {webSocket && <PingComponent connectionType="sharedb" socket={webSocket} />}
      <ShareDBContext.Provider value={contextValue}>{children}</ShareDBContext.Provider>
    </>
  );
};
